import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Download, FileSpreadsheet, FileText, LoaderCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../context/DashboardContext';
import { t, tx } from '../i18n';
import { buildCsvExport, downloadCsv } from './csvExport';
import { sectionFromPath, sectionOrder, type ExportSection } from './exportTypes';
import { loadExportSection, loadFullExport } from './loadExportData';
import { PrintReport } from './PrintReport';

type ExportAction = 'csv' | 'pdf' | 'full';

export function ExportControl() {
  const { pathname } = useLocation();
  const { dataSource, range, filters } = useDashboard();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<{ sections: ExportSection[]; full: boolean } | null>(null);
  const printStarted = useRef(false);
  const activeSection = sectionFromPath(pathname);

  useEffect(() => {
    if (!report || printStarted.current) return;
    printStarted.current = true;
    const timer = window.setTimeout(() => window.print(), 500);
    const finish = () => { setReport(null); setBusy(null); };
    window.addEventListener('afterprint', finish, { once: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', finish);
    };
  }, [report]);

  async function run(action: ExportAction) {
    setOpen(false);
    setError(null);
    setBusy(action);
    try {
      if (action === 'csv') {
        const section = await loadExportSection(dataSource, activeSection, range, filters);
        const output = buildCsvExport(section);
        downloadCsv(output.filename, output.content);
        setBusy(null);
        return;
      }
      const sections = action === 'full'
        ? await loadFullExport(dataSource, range, filters)
        : [await loadExportSection(dataSource, activeSection, range, filters)];
      printStarted.current = false;
      setReport({ sections, full: action === 'full' });
    } catch (cause) {
      setBusy(null);
      setError(cause instanceof Error ? tx(cause.message) : t('v1.export.failed'));
    }
  }

  return <>
    <div className="export-control">
      <button className="export-trigger" aria-haspopup="menu" aria-expanded={open} disabled={busy !== null} onClick={() => setOpen((value) => !value)}>
        {busy ? <LoaderCircle className="spin" size={15} /> : <Download size={15} />}<span>{busy ? t('v1.export.preparing') : t('v1.export.button')}</span><ChevronDown size={13} />
      </button>
      {open && <div className="export-menu" role="menu">
        <button role="menuitem" onClick={() => void run('csv')}><FileSpreadsheet size={17} /><span><strong title={t('common.abbr.CSV')}>CSV</strong><small>{t('v1.export.csvCurrent')}</small></span></button>
        <button role="menuitem" onClick={() => void run('pdf')}><FileText size={17} /><span><strong>{t('v1.export.pdfCurrent')}</strong><small>{t('v1.export.pdfCurrentSub')}</small></span></button>
        <button role="menuitem" onClick={() => void run('full')}><FileText size={17} /><span><strong>{t('v1.export.pdfFull')}</strong><small>{t('v1.export.pdfFullSub', { count: sectionOrder.length })}</small></span></button>
      </div>}
      {error && <div className="export-error" role="alert">{error}</div>}
    </div>
    {report && createPortal(<PrintReport sections={report.sections} full={report.full} />, document.body)}
  </>;
}
